/**
 * Bulk Subscription Update Service
 * Updates all users to Professional Plan with specified end date
 * 
 * WARNING: This service updates LIVE production data
 */

import { db } from '../config/firebase';
import { collection, doc, getDocs, setDoc, updateDoc, getDoc, deleteField } from 'firebase/firestore';
import { Subscription, SUBSCRIPTION_PLAN_IDS } from '../types/subscription';
import { User } from '../types/user';

// Configuration
const PROFESSIONAL_PLAN_ID = SUBSCRIPTION_PLAN_IDS.PROFESSIONAL;
const PROFESSIONAL_PLAN_PRICE = 149; // EGP
const DELAY_BETWEEN_OPERATIONS = 500; // 0.5 seconds

/**
 * Sleep function for delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Update all users to Professional Plan
 * @param endDate - End date timestamp (milliseconds since epoch as string)
 * @param onProgress - Callback function for progress updates
 */
export const updateAllUsersToProfessional = async (
  endDate: string,
  onProgress?: (current: number, total: number, userId: string, status: string) => void
): Promise<{
  success: boolean;
  totalUsers: number;
  successCount: number;
  failureCount: number;
  failures: Array<{ userId: string; reason: string }>;
  message: string;
}> => {
  try {
    console.log('🚀 Starting bulk subscription update to Professional Plan');
    console.log(`📅 End Date: ${new Date(parseInt(endDate)).toISOString()}`);

    // Get all users from Users collection
    const usersRef = collection(db, 'Users');
    const usersSnapshot = await getDocs(usersRef);

    if (usersSnapshot.empty) {
      return {
        success: false,
        totalUsers: 0,
        successCount: 0,
        failureCount: 0,
        failures: [],
        message: 'No users found in the database'
      };
    }

    const totalUsers = usersSnapshot.size;
    console.log(`✅ Found ${totalUsers} users to process`);

    let successCount = 0;
    let failureCount = 0;
    const failures: Array<{ userId: string; reason: string }> = [];

    // Process each user
    for (let i = 0; i < usersSnapshot.docs.length; i++) {
      const userDoc = usersSnapshot.docs[i];
      const userId = userDoc.id;
      const userData = userDoc.data() as User;

      if (onProgress) {
        onProgress(i + 1, totalUsers, userId, 'processing');
      }

      try {
        // Construct user name
        const userName = userData.name || 
          `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 
          userData.email || 
          '';

        // Update subscription in Subscriptions collection
        const subscriptionRef = doc(db, 'Subscriptions', userId);
        const subscriptionDoc = await getDoc(subscriptionRef);
        
        const now = Date.now();
        
        const subscriptionDocData = subscriptionDoc.exists() ? subscriptionDoc.data() : null;
        
        const subscriptionData: Partial<Subscription> = {
          userId: userId,
          userName: userName,
          userPhone: userData.phoneNumber || '',
          planId: PROFESSIONAL_PLAN_ID,
          status: 'active',
          startDate: subscriptionDocData?.startDate || now.toString(),
          endDate: endDate,
          totalPrice: PROFESSIONAL_PLAN_PRICE,
          currency: 'EGP',
          billingCycle: 'monthly',
          isActive: true,
          updatedAt: now.toString(),
          updatedBy: 'system',
          createdAt: subscriptionDocData?.createdAt || now.toString(),
          createdBy: subscriptionDocData?.createdBy || 'system'
        };

        // Set subscription (merge to preserve existing fields)
        await setDoc(subscriptionRef, subscriptionData, { merge: true });

        // Remove trialExtensionReason if it exists
        if (subscriptionDocData?.trialExtensionReason) {
          await updateDoc(subscriptionRef, {
            trialExtensionReason: deleteField()
          });
        }

        // Get the updated subscription data
        const updatedSubscriptionDoc = await getDoc(subscriptionRef);
        const updatedSubscriptionData = { 
          id: updatedSubscriptionDoc.id, 
          ...(updatedSubscriptionDoc.exists() ? updatedSubscriptionDoc.data() : {})
        } as Subscription;

        // Update subscription field in Users collection (for backward compatibility)
        try {
          const userRef = doc(db, 'Users', userId);
          await updateDoc(userRef, {
            subscription: updatedSubscriptionData,
            updatedAt: now.toString()
          });
        } catch (userUpdateError: any) {
          // If update fails, it's okay - the subscription in Subscriptions collection is the source of truth
          console.warn(`⚠️ Could not update subscription in Users collection for ${userId}:`, userUpdateError);
        }

        successCount++;
        if (onProgress) {
          onProgress(i + 1, totalUsers, userId, 'success');
        }

        console.log(`✅ Updated user ${userId} to Professional plan`);

      } catch (error: any) {
        failureCount++;
        const reason = error.message || 'Unknown error';
        failures.push({ userId, reason });
        console.error(`❌ Error updating user ${userId}:`, error);
        
        if (onProgress) {
          onProgress(i + 1, totalUsers, userId, 'failed');
        }
      }

      // Add delay between operations (except for the last one)
      if (i < usersSnapshot.docs.length - 1) {
        await sleep(DELAY_BETWEEN_OPERATIONS);
      }
    }

    const message = `Updated ${successCount} out of ${totalUsers} users. ${failureCount} failed.`;
    console.log('📊 Summary:', message);

    return {
      success: failureCount === 0,
      totalUsers,
      successCount,
      failureCount,
      failures,
      message
    };

  } catch (error: any) {
    console.error('❌ Fatal error in bulk update:', error);
    return {
      success: false,
      totalUsers: 0,
      successCount: 0,
      failureCount: 0,
      failures: [],
      message: `Fatal error: ${error.message}`
    };
  }
};
